// ArAgingBridgeChart — A/R aging bridge with stacked start/end bars and bucket
// movement steps. It keeps the overview figure tied to the same period/bucket
// artifact data as the customer change tables.

import type { ArAgingBridge, ArAgingBridgeBucket } from '../cfoArView'
import { computeArAgingBridgeColumns } from './ArAgingBridgeModel'

export type ArAgingBridgeChartProps = {
  bridge: ArAgingBridge
  ariaLabel: string
  formatValue?: (value: number) => string
  width?: number
  height?: number
  className?: string
}

const toneColor = (tone: ArAgingBridgeBucket['tone'], muted = false): string => {
  if (tone === 'risk') return muted ? 'rgba(192, 73, 47, 0.28)' : 'var(--neg, #c0492f)'
  if (tone === 'watch') return muted ? 'rgba(183, 121, 31, 0.28)' : 'var(--check-acc, #b7791f)'
  return muted ? 'rgba(14, 124, 134, 0.25)' : 'var(--pos, #0e7c86)'
}

const signed = (value: number, fmt: (value: number) => string): string =>
  Math.abs(value) < 0.5 ? fmt(0) : `${value > 0 ? '+' : '\u2212'}${fmt(Math.abs(value))}`

export function ArAgingBridgeChart({
  bridge,
  ariaLabel,
  formatValue,
  width = 920,
  height = 292,
  className,
}: ArAgingBridgeChartProps) {
  const columns = computeArAgingBridgeColumns(bridge)
  const fmt = (value: number) => (formatValue ? formatValue(value) : String(Math.round(value)))
  const padTop = 28
  const padBottom = 54
  const padX = 42
  const plotH = height - padTop - padBottom
  const values = columns.flatMap((column) => column.kind === 'delta' ? [column.base, column.top] : [0, column.total])
  const max = Math.max(...values, 1)
  const toY = (value: number) => padTop + (1 - Math.max(0, value) / max) * plotH
  const baseY = toY(0)
  const slot = (width - padX * 2) / columns.length
  const anchorW = Math.min(94, slot * 0.62)
  const deltaW = Math.min(74, slot * 0.52)

  return (
    <svg className={className} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={ariaLabel}>
      <line x1={padX} y1={baseY} x2={width - padX} y2={baseY} stroke="var(--axis, #3a3f47)" strokeWidth={1.2} />
      {columns.map((column, i) => {
        const cx = padX + slot * i + slot / 2
        if (column.kind !== 'delta') {
          const x = cx - anchorW / 2
          let yCursor = baseY
          return (
            <g key={`${column.kind}-${column.label}`}>
              {column.segments.filter((segment) => segment.value > 0.005).map((segment) => {
                const y = toY((baseY - yCursor) / plotH * max + segment.value)
                const h = Math.max(1, yCursor - y)
                yCursor = y
                return (
                  <g key={`${column.kind}-${segment.label}`}>
                    <rect x={x} y={y} width={anchorW} height={h} rx={2} fill={toneColor(segment.tone)} opacity={0.92} />
                    {h >= 22 ? (
                      <text
                        x={cx}
                        y={y + h / 2 + 4}
                        textAnchor="middle"
                        style={{ font: '700 10px var(--font-ui)', fill: segment.tone === 'watch' ? 'var(--ink, #16242c)' : '#fff' }}
                      >
                        {segment.label} {fmt(segment.value)}
                      </text>
                    ) : null}
                  </g>
                )
              })}
              <text x={cx} y={toY(column.total) - 8} textAnchor="middle" style={{ font: '800 12px var(--font-ui)', fill: 'var(--ink, #16242c)' }}>
                {fmt(column.total)}
              </text>
              <text x={cx} y={height - 28} textAnchor="middle" style={{ font: '600 11px var(--font-guide)', fontStyle: 'italic', fill: 'var(--muted, #868c9a)' }}>
                {column.label}
              </text>
            </g>
          )
        }
        const prev = columns[i - 1]
        const x = cx - deltaW / 2
        const y = toY(column.top)
        const h = Math.max(2, toY(column.base) - toY(column.top))
        const isDown = column.delta < 0
        const priorCx = padX + slot * (i - 1) + slot / 2
        const priorW = prev?.kind === 'delta' ? deltaW : anchorW
        return (
          <g key={`delta-${column.label}`}>
            <line
              x1={priorCx + priorW / 2}
              y1={toY(isDown ? column.top : column.base)}
              x2={x}
              y2={toY(isDown ? column.top : column.base)}
              stroke="var(--hair-strong, #c8cdd5)"
              strokeDasharray="4 4"
              strokeWidth={1}
            />
            <rect
              x={x}
              y={y}
              width={deltaW}
              height={h}
              rx={2}
              fill={toneColor(column.tone, isDown)}
              stroke={isDown ? toneColor(column.tone) : 'none'}
              strokeWidth={isDown ? 1.3 : 0}
            />
            <text
              x={cx}
              y={isDown ? toY(column.base) + 14 : y - 7}
              textAnchor="middle"
              style={{ font: '800 11px var(--font-ui)', fill: isDown ? 'var(--pos, #047857)' : column.tone === 'risk' ? 'var(--neg-ink, #a83e29)' : 'var(--ink-2, #4c5360)' }}
            >
              {signed(column.delta, fmt)}
            </text>
            <text
              x={cx}
              y={height - 28}
              textAnchor="middle"
              style={{ font: '600 11px var(--font-guide)', fontStyle: 'italic', fill: column.tone === 'risk' ? 'var(--neg-ink, #a83e29)' : 'var(--muted, #868c9a)' }}
            >
              {column.label}
            </text>
          </g>
        )
      })}
    </svg>
  )
}

export default ArAgingBridgeChart
